import { TrendingUp, AlertCircle, CheckCircle, Clock } from 'lucide-react';

export function PerformanceTab({ data }: { data: any }) {
  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-[#6B7280]">No performance data available</p>
      </div>
    );
  }

  const { mrr, userGrowth, engagement, churn, feedback, alerts } = data;

  const healthMetrics = [
    {
      name: 'Revenue Health',
      score: mrr?.current_mrr > 0 ? Math.min(100, (mrr.current_mrr / 1000) * 10) : 0,
      status: mrr?.current_mrr > 500 ? 'excellent' : mrr?.current_mrr > 100 ? 'good' : 'needs improvement'
    },
    {
      name: 'User Growth',
      score: userGrowth ? Math.min(100, (userGrowth.this_week_signups / Math.max(userGrowth.last_week_signups, 1)) * 100) : 0,
      status: userGrowth && userGrowth.this_week_signups > userGrowth.last_week_signups ? 'excellent' : 'needs improvement'
    },
    {
      name: 'Engagement',
      score: engagement ? Math.min(100, (engagement.dau_today / Math.max(engagement.mau, 1)) * 100 * 5) : 0,
      status: engagement && engagement.dau_today > engagement.dau_yesterday ? 'excellent' : 'good'
    },
    {
      name: 'Retention',
      score: churn ? Math.max(0, 100 - (churn.churn_rate_30d * 10)) : 0,
      status: churn && churn.churn_rate_30d < 5 ? 'excellent' : churn && churn.churn_rate_30d < 10 ? 'good' : 'needs improvement'
    }
  ];

  const overallHealth = healthMetrics.reduce((sum, metric) => sum + metric.score, 0) / healthMetrics.length;

  const getHealthColor = (score: number) => {
    if (score >= 80) return { bg: 'bg-[#D1FAE5]', text: 'text-[#065F46]', bar: 'bg-[#10B981]' };
    if (score >= 60) return { bg: 'bg-[#E8F2F0]', text: 'text-[#8CAFA8]', bar: 'bg-[#8CAFA8]' };
    if (score >= 40) return { bg: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-500' };
    return { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' };
  };

  const overallHealthColor = getHealthColor(overallHealth);

  return (
    <div className="space-y-6">
      <div className={`${overallHealthColor.bg} p-6 rounded-[16px] border-2 ${overallHealthColor.bar.replace('bg-', 'border-')}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-[#1F2937]">Overall Platform Health</h3>
          <div className={`text-5xl font-bold ${overallHealthColor.text}`}>
            {Math.round(overallHealth)}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className={`${overallHealthColor.bar} h-4 rounded-full transition-all`}
            style={{ width: `${overallHealth}%` }}
          ></div>
        </div>
        <p className="text-sm text-[#6B7280] mt-2">
          Score out of 100 based on revenue, growth, engagement, and retention metrics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {healthMetrics.map((metric) => {
          const colors = getHealthColor(metric.score);
          return (
            <div key={metric.name} className={`${colors.bg} p-6 rounded-[16px] border ${colors.bar.replace('bg-', 'border-')}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#1F2937]">{metric.name}</h4>
                <span className={`text-sm px-3 py-1 rounded-full ${colors.bg} ${colors.text} font-medium`}>
                  {metric.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mb-2">
                <div className={`text-4xl font-bold ${colors.text}`}>
                  {Math.round(metric.score)}
                </div>
                {metric.score >= 80 ? (
                  <CheckCircle className={colors.text} size={32} />
                ) : metric.score >= 40 ? (
                  <Clock className={colors.text} size={32} />
                ) : (
                  <AlertCircle className={colors.text} size={32} />
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`${colors.bar} h-2 rounded-full transition-all`}
                  style={{ width: `${metric.score}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Key Metrics Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-[#6B7280] mb-3">Revenue & Subscriptions</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-[#6B7280]">Current MRR</span>
                <span className="font-medium text-[#1F2937]">${mrr?.current_mrr || 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#6B7280]">Active Subs</span>
                <span className="font-medium text-[#1F2937]">{mrr?.subscription_count || 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#6B7280]">Churn Rate</span>
                <span className="font-medium text-[#1F2937]">{churn?.churn_rate_30d?.toFixed(1) || 0}%</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium text-[#6B7280] mb-3">User Metrics</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-[#6B7280]">Total Users</span>
                <span className="font-medium text-[#1F2937]">{userGrowth?.total_users || 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#6B7280]">This Week</span>
                <span className="font-medium text-[#1F2937]">{userGrowth?.this_week_signups || 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#6B7280]">This Month</span>
                <span className="font-medium text-[#1F2937]">{userGrowth?.this_month_signups || 0}</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium text-[#6B7280] mb-3">Engagement</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className="text-[#6B7280]">DAU</span>
                <span className="font-medium text-[#1F2937]">{engagement?.dau_today || 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#6B7280]">MAU</span>
                <span className="font-medium text-[#1F2937]">{engagement?.mau || 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#6B7280]">Response Rate</span>
                <span className="font-medium text-[#1F2937]">{feedback?.overall_response_rate?.toFixed(1) || 0}%</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {alerts && alerts.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 p-6 rounded-[16px]">
          <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center gap-2">
            <AlertCircle size={20} />
            Active Alerts ({alerts.length})
          </h3>
          <div className="space-y-3">
            {alerts.slice(0, 5).map((alert: any) => (
              <div
                key={alert.id}
                className={`p-4 rounded-[12px] ${
                  alert.severity === 'critical'
                    ? 'bg-red-100 border border-red-300'
                    : alert.severity === 'warning'
                    ? 'bg-orange-100 border border-orange-300'
                    : 'bg-[#E8F2F0] border border-[#8CAFA8]'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-[#1F2937]">{alert.title}</h4>
                    <p className="text-sm text-[#374151] mt-1">{alert.message}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ml-3 ${
                    alert.severity === 'critical'
                      ? 'bg-red-200 text-red-800'
                      : alert.severity === 'warning'
                      ? 'bg-orange-200 text-orange-800'
                      : 'bg-[#8CAFA8]/20 text-[#1F2937]'
                  }`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#E8F2F0] border border-[#8CAFA8]/30 p-6 rounded-[16px]">
        <h4 className="font-medium text-[#1F2937] mb-3 flex items-center gap-2">
          <TrendingUp size={20} />
          Performance Recommendations
        </h4>
        <ul className="space-y-2 text-sm text-[#374151]">
          {overallHealth >= 80 && (
            <li>Platform is performing excellently. Continue monitoring key metrics.</li>
          )}
          {overallHealth < 80 && overallHealth >= 60 && (
            <li>Platform is healthy but has room for improvement in some areas.</li>
          )}
          {overallHealth < 60 && (
            <li>Several metrics need attention. Focus on improving underperforming areas.</li>
          )}
          {churn && churn.churn_rate_30d > 5 && (
            <li>Churn rate is elevated. Consider implementing retention strategies.</li>
          )}
          {userGrowth && userGrowth.this_week_signups < userGrowth.last_week_signups && (
            <li>User growth has slowed. Review acquisition channels and marketing efforts.</li>
          )}
          {engagement && engagement.dau_today < engagement.dau_yesterday && (
            <li>Daily active users decreased. Focus on engagement and retention features.</li>
          )}
          {feedback && feedback.overall_response_rate < 50 && (
            <li>Feedback response rate is low. Consider improving the feedback request process.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
